/**
 * ScheduleEditor: a 7-row grid of per-day active-capture windows.
 *
 * Each row is one weekday. A day can hold zero or more windows; each window
 * is a pair of `<input type="time">` pickers plus a Remove control. The
 * "+ Window" button appends a new default 09:00-17:00 window to that day.
 *
 * The component is fully controlled — parent owns the `WeeklySchedule` value
 * and receives updates via `onChange`. Time strings match the Rust
 * `chrono::NaiveTime` deserializer: we always emit `HH:MM:SS` (the browser
 * time picker returns `HH:MM`, so we append `:00`).
 */

import type { JSX } from 'preact';
import { WEEKDAYS, type TimeWindow, type WeeklySchedule } from '../data/config';

interface Props {
  value: WeeklySchedule;
  onChange: (next: WeeklySchedule) => void;
}

const DAY_LABEL: Record<keyof WeeklySchedule, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

/** `HH:MM:SS` -> `HH:MM` for the picker input. */
function toPickerValue(hhmmss: string): string {
  const parts = hhmmss.split(':');
  return parts.length >= 2
    ? `${parts[0] ?? '00'}:${parts[1] ?? '00'}`
    : '00:00';
}

/** `HH:MM` from the picker -> `HH:MM:SS` on the wire. */
function fromPickerValue(hhmm: string): string {
  return `${hhmm}:00`;
}

export function ScheduleEditor({ value, onChange }: Props): JSX.Element {
  function updateDay(day: keyof WeeklySchedule, windows: TimeWindow[]): void {
    onChange({ ...value, [day]: windows });
  }

  function addWindow(day: keyof WeeklySchedule): void {
    const next: TimeWindow = { from: '09:00:00', to: '17:00:00' };
    updateDay(day, [...value[day], next]);
  }

  function removeWindow(day: keyof WeeklySchedule, index: number): void {
    const wins = value[day].slice();
    wins.splice(index, 1);
    updateDay(day, wins);
  }

  function updateWindow(
    day: keyof WeeklySchedule,
    index: number,
    patch: Partial<TimeWindow>,
  ): void {
    const wins = value[day].slice();
    const cur = wins[index];
    if (!cur) return;
    wins[index] = { ...cur, ...patch };
    updateDay(day, wins);
  }

  return (
    <div class="space-y-2" data-testid="schedule-editor">
      {WEEKDAYS.map((day) => {
        const windows = value[day];
        return (
          <div
            key={day}
            class="grid grid-cols-[64px_1fr_auto] items-start gap-3 rounded border border-surface-muted/20 bg-surface p-3"
          >
            <div class="pt-1 font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted">
              {DAY_LABEL[day]}
            </div>
            <div class="space-y-2">
              {windows.length === 0 ? (
                <div class="text-sm text-surface-muted">No active windows</div>
              ) : null}
              {windows.map((w, i) => (
                <div
                  key={`${day}-${String(i)}`}
                  class="flex flex-wrap items-center gap-2 text-sm"
                  data-testid={`window-${day}-${String(i)}`}
                >
                  <input
                    type="time"
                    aria-label={`${DAY_LABEL[day]} window ${String(i + 1)} start`}
                    class="h-9 rounded border border-surface-muted/40 bg-surface px-2"
                    value={toPickerValue(w.from)}
                    onInput={(e) => {
                      updateWindow(day, i, {
                        from: fromPickerValue(
                          (e.target as HTMLInputElement).value,
                        ),
                      });
                    }}
                  />
                  <span class="text-surface-muted">to</span>
                  <input
                    type="time"
                    aria-label={`${DAY_LABEL[day]} window ${String(i + 1)} end`}
                    class="h-9 rounded border border-surface-muted/40 bg-surface px-2"
                    value={toPickerValue(w.to)}
                    onInput={(e) => {
                      updateWindow(day, i, {
                        to: fromPickerValue(
                          (e.target as HTMLInputElement).value,
                        ),
                      });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      removeWindow(day, i);
                    }}
                    aria-label={`Remove ${DAY_LABEL[day]} window ${String(i + 1)}`}
                    class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted hover:text-signal transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                addWindow(day);
              }}
              aria-label={`Add ${DAY_LABEL[day]} window`}
              class="h-9 rounded border border-surface-muted/40 bg-surface px-3 text-sm font-medium hover:bg-surface-muted/10"
            >
              + Window
            </button>
          </div>
        );
      })}
    </div>
  );
}
